import React, { useEffect, useState } from "react";
import { fetchQuranicQuiz, fetchBuiltinQuranicQuiz, submitQuranicQuiz, submitBuiltinQuranicQuiz } from "../api";

const PASS_THRESHOLD = 0.9;

/**
 * QuranicQuiz — completely isolated quiz engine for Quranic topics.
 * Does NOT use the existing SRS/quiz system.
 * Supports both user-created topics and built-in curriculum topics via isBuiltin prop.
 */
export default function QuranicQuiz({ topicId, isBuiltin, topicName, onExit, onComplete, onRetake, retake = false }) {
  const [questions, setQuestions] = useState([]);
  const [newWordNorms, setNewWordNorms] = useState([]);
  const [quizReason, setQuizReason] = useState(null); // "ok" | "all_seen" | "fetch_error" | null
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const [passed, setPassed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [retryKey, setRetryKey] = useState(0); // internal retry for user topics

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError(null);
        setCurrentIdx(0);
        setSelected(null);
        setScore(0);
        setFinished(false);
        setPassed(false);
        const data = isBuiltin
          ? await fetchBuiltinQuranicQuiz(topicId, retake)
          : await fetchQuranicQuiz(topicId);

        if (isBuiltin) {
          setQuestions(data.questions || []);
          setNewWordNorms(data.new_word_norms || []);
          setQuizReason(data.reason || "ok");
        } else {
          setQuestions(data.questions || []);
          setQuizReason("ok");
        }
      } catch (err) {
        setError(err.message || "Could not load quiz");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [topicId, isBuiltin, retake, retryKey]);

  const current = questions[currentIdx];

  const handleChoice = (idx) => {
    if (selected !== null) return;
    setSelected(idx);
    if (idx === current.correct_index) setScore((s) => s + 1);
  };

  const handleNext = async () => {
    if (currentIdx + 1 >= questions.length) {
      await doSubmit(score, questions.length, newWordNorms);
    } else {
      setCurrentIdx((i) => i + 1);
      setSelected(null);
    }
  };

  const doSubmit = async (finalScore, total, wordNorms) => {
    setSubmitting(true);
    try {
      let result;
      if (isBuiltin) {
        result = await submitBuiltinQuranicQuiz(topicId, finalScore, total, wordNorms);
      } else {
        result = await submitQuranicQuiz(topicId, finalScore, total);
      }
      const didPass = result?.passed ?? (total === 0);
      setPassed(didPass);
      if (didPass) {
        onComplete && onComplete(finalScore, total);
      }
    } catch {
      // Still show finished screen on error
    }
    setSubmitting(false);
    setFinished(true);
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-16 gap-3">
      <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      <p className="text-sm text-slate-500">Loading quiz…</p>
    </div>
  );

  if (error) return (
    <div className="p-4 text-center">
      <p className="text-red-600 mb-4">{error}</p>
      <button onClick={onExit} className="px-4 py-2 bg-slate-200 rounded-lg text-sm">Back</button>
    </div>
  );

  // All vocabulary already seen — offer retake or mark complete
  if (isBuiltin && quizReason === "all_seen") return (
    <div className="p-6 text-center">
      <div className="text-5xl mb-4">⭐</div>
      <h2 className="text-xl font-bold text-slate-800 mb-2">All Vocabulary Covered!</h2>
      <p className="text-slate-600 mb-6">
        You've already been quizzed on all the vocabulary in this topic from previous lessons.
      </p>
      <div className="flex flex-col gap-3">
        <button
          onClick={() => onRetake && onRetake()}
          className="px-6 py-2.5 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800 transition-colors"
        >
          🔄 Retake Quiz from Beginning
        </button>
        <button
          onClick={() => doSubmit(0, 0, [])}
          disabled={submitting}
          className="px-6 py-2.5 border border-emerald-300 text-emerald-700 rounded-xl font-medium hover:bg-emerald-50 transition-colors disabled:opacity-60 text-sm"
        >
          {submitting ? "Saving…" : "Mark as Complete ✓"}
        </button>
        <button onClick={onExit} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700">
          Back
        </button>
      </div>
    </div>
  );

  if (quizReason === "fetch_error" || questions.length === 0) return (
    <div className="p-4 text-center">
      <p className="text-slate-600 mb-4">No quiz questions available for this topic.</p>
      <button onClick={onExit} className="px-4 py-2 bg-slate-200 rounded-lg text-sm">Back</button>
    </div>
  );

  if (finished) {
    const pct = questions.length > 0 ? Math.round((score / questions.length) * 100) : 0;
    return (
      <div className="p-6 text-center">
        <div className="text-5xl mb-4">{passed ? "🌙" : "📚"}</div>
        <h2 className="text-xl font-bold text-slate-800 mb-2">
          {passed ? "Quiz Complete!" : "Not Passed"}
        </h2>
        <p className="text-slate-600 mb-1">
          You scored{" "}
          <span className={`font-bold ${passed ? "text-emerald-600" : "text-red-500"}`}>{score}</span>
          {" "}out of <span className="font-bold">{questions.length}</span>
          {" "}({pct}%)
        </p>
        {passed ? (
          <p className="text-sm text-emerald-600 font-medium mb-6">✓ Topic marked as completed.</p>
        ) : (
          <p className="text-sm text-slate-500 mb-6">
            You need {Math.round(PASS_THRESHOLD * 100)}% or higher to complete this topic.
          </p>
        )}
        <div className="flex flex-col gap-3">
          {!passed && (
            <button
              onClick={() => {
                if (isBuiltin && onRetake) {
                  onRetake();
                } else {
                  setRetryKey((k) => k + 1);
                }
              }}
              className="px-6 py-2.5 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800 transition-colors"
            >
              🔄 Try Again
            </button>
          )}
          <button
            onClick={onExit}
            className="px-6 py-2.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors"
          >
            Back to Topic
          </button>
          {passed && isBuiltin && (
            <button
              onClick={() => onRetake && onRetake()}
              className="px-6 py-2.5 border border-slate-300 text-slate-600 rounded-xl font-medium hover:bg-slate-50 transition-colors text-sm"
            >
              🔄 Retake Quiz from Beginning
            </button>
          )}
        </div>
      </div>
    );
  }

  const isCorrect = selected === current.correct_index;
  const choiceColors = (idx) => {
    if (selected === null) return "cursor-pointer border-slate-200 hover:border-emerald-400 hover:bg-emerald-50";
    if (idx === current.correct_index) return "cursor-default border-emerald-500 bg-emerald-50 text-emerald-800";
    if (idx === selected) return "cursor-default border-red-400 bg-red-50 text-red-800";
    return "cursor-default border-slate-200 text-slate-400";
  };

  return (
    <div className="flex flex-col gap-4 max-w-xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={onExit} className="text-slate-500 hover:text-slate-700 text-sm flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Exit
        </button>
        <span className="text-sm font-medium text-slate-600">
          {currentIdx + 1} / {questions.length}
        </span>
        <div className="w-12 text-right">
          <span className="text-sm font-bold text-emerald-600">{score} ✓</span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full bg-emerald-500 transition-all duration-300"
          style={{ width: `${((currentIdx + 1) / questions.length) * 100}%` }}
        />
      </div>

      {/* Question */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
        <p className="text-xs font-medium text-slate-400 mb-2 uppercase tracking-wide">
          {current.type === "word_meaning" ? "Word Meaning" : "Word Recognition"}
        </p>
        <p className="text-lg font-semibold text-slate-800 mb-1">{current.prompt_en}</p>
        {current.type === "word_meaning" && (
          <p className="text-2xl font-arabic text-slate-700 mt-2" dir="rtl">{current.prompt_ar}</p>
        )}
        <p className="text-xs text-slate-400 mt-1">Verse {current.verse_key}</p>
      </div>

      {/* Choices */}
      <div className="grid grid-cols-1 gap-2">
        {(current.choices || []).map((choice, idx) => (
          <button
            key={idx}
            onClick={() => handleChoice(idx)}
            disabled={selected !== null}
            className={`w-full text-left px-4 py-3 rounded-xl border-2 text-sm font-medium transition-colors ${choiceColors(idx)}`}
            dir={current.type === "word_recognition" ? "rtl" : "ltr"}
          >
            {choice}
          </button>
        ))}
      </div>

      {/* Feedback + Next */}
      {selected !== null && (
        <div className={`rounded-xl p-3 text-sm font-medium ${isCorrect ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`}>
          {isCorrect ? "✓ Correct!" : `✗ Correct answer: ${current.choices[current.correct_index]}`}
        </div>
      )}

      {selected !== null && (
        <button
          onClick={handleNext}
          disabled={submitting}
          className="w-full py-3 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-60"
        >
          {submitting ? "Saving…" : currentIdx + 1 >= questions.length ? "Finish Quiz" : "Next →"}
        </button>
      )}
    </div>
  );
}


